/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, ComponentProps, VNode } from 'preact';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'preact/hooks';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { classNames } from '../utils/UNSAFE_classNames';

import {
  FocusableComponentHandle,
  useMessageFocusManager
} from '../hooks/PRIVATE_useMessagesFocusManager';
import { getRenderer, Message, MessagesManager } from '../PRIVATE_Message';
import { Flex } from '../UNSAFE_Flex';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import { Item, ItemMetadata } from '../utils/UNSAFE_dataProvider';
import { Handle } from '../utils/UNSAFE_typeUtils';
import { MessageBannerRedwoodTheme } from './themes/redwood/MessageBannerTheme';

type MessageRef = Handle<typeof Message, 'messageRef'>;

// TODO: Refactor Message Item types
// 

/**
 * An object representing a single message in MessageBanner component.
 */
export type MessageBannerItem = {
  /**
   * Defines whether or not to include the close icon for the message
   */
  closeAffordance?: 'on' | 'off';

  /**
   * Defines the detail text of the message
   */
  detail?: string;

  /**
   * Defines the severity of the message
   */
  severity?: 'none' | 'error' | 'warning' | 'confirmation' | 'info';

  /**
   * Defines the sound to be played when opening the message
   */
  sound?: 'default' | 'none' | string;

  /**
   * Defines the primary text of the message
   */
  summary?: string;

  /**
   * Defines the timestamp for the message in ISO format
   */
  timestamp?: string;
};

/**
 * Structure of data item passed to the renderers
 */
export type MessageBannerRendererDataItem<K, D> = {
  /**
   * The data for the current message
   */
  data: D;

  /**
   * The zero-based index of the current message
   */
  index: number;

  /**
   * The key for the current message
   */
  key: K;

  /**
   * The metadata for the current message
   */
  metadata?: ItemMetadata<K>;
};

/**
 * Props for the MessageBanner Component
 */
type Props<Key, Data> = {
  /**
   * Data for the MessageBanner component. This data is used for rendering each banner message.
   * The key for each message will be configured using the key of the corresponding item. This way,
   * the component will know whether a new message is being added or an existing message is being updated/removed
   * when the new data comes in.
   */
  data: Item<Key, Data>[];

  /**
   * A Banner message can have a different look and feel. For example, when using page-level
   * messaging the messages need to be rendered from edge to edge without any outline. On the other
   * hand, when they are being used in a section of a page or a dialog, they need to be rendered
   * with an outline. This attribute can be used to specify where the component is being used so that
   * it will render the messages accordingly.
   */
  type?: 'page' | 'section';

  /**
   * Triggered when a user tries to close a message through UI interaction. The application
   * should listen to this event and remove the corresponding message item from the data
   * which would then result in the message closed. If the application
   * fails to remove the message item from the data, then no change will be done in the
   * UI by the component and the message will stay in the UI opened.
   */
  onClose?: (item: Item<Key, Data>) => void;

  /**
   * Applications can use this property to provide the key of a renderer or a function that
   * returns the key of a renderer to use for rendering the detail content.
   *
   * When a renderer key is provided as a value for this property, the corresponding renderer
   * will be used for rendering the detail content for all the messages. If applications want
   * to use a different renderer for different messages, they can provide a function that
   * returns a renderer key instead.
   *
   * The provided function should accept an Item and return a key to a renderer for
   * rendering the corresponding message's detail content. The value returned from this function
   * should be a key to one of the renderers provided. If the returned value is not
   * one of the keys of the provided renderers, the component will throw an Error.
   *
   * If the function returns undefined, the component then will perform default rendering
   * of the detail content using the detail property of the corresponding message.
   *
   * If an application specifies both detail and a valid detailRendererKey, the detailRendererKey will
   * take precedence and the corresponding renderer will be used for rendering the detail content.
   */
  detailRendererKey?: string | ((item: Item<Key, Data>) => string | undefined);

  /**
   * A set of available renderers for rendering the message content. Which renderer is used
   * for rendering which content will be decided by specific properties in the row data.
   */
  renderers?: Record<string, (data: MessageBannerRendererDataItem<Key, Data>) => ComponentChildren>;

  /**
   * A renderer that renders the close button in the message.
   */
  closeButtonRenderer?: (title: string, onAction: (event?: Event) => void) => VNode;

  // Refactor types (cannot import from PRIVATE modules)
  // 
  /**
   * Translations resources
   */
  translations?: {
    /**
     * Label for the message close button
     */
    close?: string;

    /**
     * Text for 'error' severity level
     */
    error?: string;

    /**
     * Text for 'warning' severity level
     */
    warning?: string;

    /**
     * Text for 'info' severity level
     */
    info?: string;

    /**
     * Text for 'confirmation' severity level
     */
    confirmation?: string;

    /**
     * Text to guide keyboard users navigate to prior focussed element when the messages
     * region gains focus
     */
    navigationFromMessagesRegion?: string;

    /**
     * Text to guide keyboard users navigate to new displayed messages when focus is outside
     * the messages region
     */
    navigationToMessagesRegion?: string;
  };
};

/**
 * Animation states for banner messages
 * TODO: Get this from theme context provider
 */
const animationStates: ComponentProps<typeof MessagesManager>['animationStates'] = {
  entering: (node) => ({
    to: {
      maxHeight: `${node.scrollHeight}px`,
      overflow: 'hidden'
    },
    options: {
      duration: 250
    },
    end: {
      maxHeight: 'none',
      overflow: 'initial'
    }
  }),
  exiting: (node) => ({
    ...(node.style.maxHeight === 'none' && {
      from: {
        maxHeight: `${node.scrollHeight}px`,
        overflow: 'hidden'
      }
    }),
    to: {
      maxHeight: 0,
      overflow: 'hidden'
    },
    end: {
      maxHeight: 0,
      overflow: 'hidden'
    },
    options: {
      duration: 250
    }
  })
};
const initialAnimationStyles = {
  maxHeight: 0,
  overflow: 'hidden'
} as const;

/**
 * Renders individual messages based on the provided data
 */
function MessageBanner<
  K extends string | number = string | number,
  D extends MessageBannerItem = MessageBannerItem
>({
  closeButtonRenderer,
  detailRendererKey,
  data,
  onClose,
  renderers,
  translations,
  type = 'section'
}: Props<K, D>) {
  // Keyboard Navigation and Focus Management
  const messagesRef = useRef(new Map<K, MessageRef | null>());
  const containerDivRef = useRef<HTMLDivElement>(null);
  const focusHandleRef = useRef<FocusableComponentHandle>(null);
  const [liveRegionText, setLiveRegionText] = useState<string>();
  const [shouldRender, setShouldRender] = useState(data.length > 0);
  // We need a ref that holds the current & previous data length, as the exiting
  // node will always call handleNextFocus with previous data.
  // As in TransitionGroup, when an item is removed from the data, a new vnode
  // will not be created instead previous vnode will be used. So, the new handleNextFocus
  // will not be called when the old vnode exits. Thus, we will be using a ref
  // to always get the correct current data length.
  const dataLengthRef = useRef(data.length);
  const prevDataLengthRef = useRef(0);
  // Update the data length ref
  dataLengthRef.current = data.length;

  // We will be using a function based ref to store the refs in the Map
  const setMessageRef = useCallback(
    // TODO: use ramda.curry here (currently it is throwing ts error when used with generics)
    (key: K) => {
      return (ref: MessageRef | null) => messagesRef.current.set(key, ref);
    },
    []
  );

  // Update the focusHandleRef
  useImperativeHandle(
    focusHandleRef,
    () => ({
      focus: () => {
        // Only trigger focus if the component is rendering messages
        if (data.length) {
          const firstItemKey = data[0].key;
          messagesRef.current.get(firstItemKey)?.focus();
          return true;
        }
        return false;
      },
      contains: (element: Element) => {
        // Only invoke method if the component is rendering messages
        if (data.length && element) {
          return containerDivRef.current?.contains(element) ?? false;
        }
        return false;
      }
    }),
    [data]
  );

  // Register handlers for focus management
  const { controller, handlers } = useMessageFocusManager(focusHandleRef, {
    onFocus: useCallback(() => {
      setLiveRegionText(translations?.navigationFromMessagesRegion);
    }, [setLiveRegionText, translations])
  });

  /**
   * Emits onClose event for the provided message.
   * @param item The message which the user tried to close
   */
  const handleClose = useCallback(
    (item: Item<K, D>) => {
      onClose?.(item);
    },
    [onClose]
  );

  /**
   * Handles focus when a message is closed and animated away from the DOM
   * @param key The key of the message
   * @param index The index of the message
   */
  const handleNextFocus = useCallback(
    (_key: K, index: number, closedMessageNode?: Element) => {
      const isClosedMessageFocused = closedMessageNode?.contains(document.activeElement);

      // If there are no messages, do not render anything. As the old messages
      // are still in the DOM, use the data count to determine what to do next as it
      // represents the next state.
      if (dataLengthRef.current === 0) {
        setShouldRender(false);
        // If the current message holds focus, then restore previous focus
        if (isClosedMessageFocused) {
          controller.restorePriorFocus();
        }
        return;
      }

      // In TransitionGroup, when an item is removed from the data, a new vnode
      // will not be created instead previous vnode will be used. So, the new handleNextFocus
      // will not be called when the old vnode exits. Thus, all the deps will not be pointing to
      // the current references, rather they will be pointing to the ones where this vnode
      // last existed in the data.
      // This means, the data will contain the closing message as well. But, all the ref objects
      // still point to the most up-to-date values. With that in mind, we will be evaluating the
      // following values.
      const renderedMessagesCount = data.length;

      // Now that this message is closed, focus the next message that will take this index. If no
      // message will take this message's index, then it means that this is the last message. If
      // that is the case, focus the message at the previous index.
      // Use the count of the messages that are currently shown in the UI (current state including
      // the message that will be removed). This way we can get the correct item from the data
      // as it will contain the closing message as well.
      const nextMessageIndexToFocus = index + 1 < renderedMessagesCount ? index + 1 : index - 1;

      // if next message is available then transfer the focus to the next element
      if (nextMessageIndexToFocus > -1 && isClosedMessageFocused) {
        const nextMessageKey = data[nextMessageIndexToFocus].key;
        messagesRef.current.get(nextMessageKey)?.focus();
      }
    },
    [controller, data]
  );

  // Prioritize this component whenever the data changes and
  // the new data has at least one message
  useEffect(() => {
    if (data.length) {
      // set state to render content whenever the data is not empty
      setShouldRender(true);
      if (data.length > prevDataLengthRef.current) {
        // Only when having a new message, update the aria-live area with the
        // text to indicate how to get the focus to the new message.
        setLiveRegionText(translations?.navigationToMessagesRegion);
      }
      controller.prioritize();
    } else {
      // When there are no messages, clear the live region so that
      // the navigation text will be read when a new message appear
      setLiveRegionText('');
    }
    prevDataLengthRef.current = data.length;
  }, [controller, data, translations]);

  // When both shouldRender flag is false and no data to render, do not render
  // anything
  if (!shouldRender && data.length === 0) {
    return null;
  }

  const { classes } = useComponentTheme(MessageBannerRedwoodTheme);

  return (
    <div
      ref={containerDivRef}
      class={classNames(['oj-c-messagebanner', classes])}
      tabIndex={-1} // Need to add tabindex to make the container receive the focus events
      {...handlers}>
      <Flex direction="column" gap={type === 'section' ? '1x' : undefined}>
        <MessagesManager
          animationStates={animationStates}
          initialAnimationStyles={initialAnimationStyles}
          data={data}
          onMessageWillRemove={handleNextFocus}>
          {({ index, item }) => (
            <Message
              messageRef={setMessageRef(item.key)}
              item={item}
              closeButtonRenderer={closeButtonRenderer}
              detailRenderer={getRenderer(item, detailRendererKey, renderers)}
              index={index}
              key={item.key}
              variant={type === 'page' ? 'pageBanner' : 'sectionBanner'}
              onClose={handleClose}
              translations={translations}
            />
          )}
        </MessagesManager>
        <LiveRegion>{liveRegionText}</LiveRegion>
      </Flex>
    </div>
  );
}

export { MessageBanner };
